"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, ShieldCheck, Wrench, FileCheck2, PhoneCall, Clock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const steps = [{
  stepNumber: "01",
  title: "Rapid assessment & scope",
  description: "Fast triage of tenant units, mechanical rooms, and common areas. Our technicians deploy infrared moisture mapping and air quality meters immediately upon arrival to define the exact loss perimeter.",
  badge: "Immediate on-site triage",
  icon: Search
}, {
  stepNumber: "02",
  title: "Mitigation & management coordination",
  description: "Immediate physical containment, quiet daytime extraction protocols, and direct coordination with property managers, board members, and building engineers to keep operations running.",
  badge: "Minimal tenant disruption",
  icon: ShieldCheck
}, {
  stepNumber: "03",
  title: "Restoration & structural repairs",
  description: "Commercial-grade desiccant dehumidification, controlled HEPA air scrubbing, soot neutralization, and licensed structural rebuild under a single unified project lead.",
  badge: "Certified industrial drying",
  icon: Wrench
}, {
  stepNumber: "04",
  title: "Documentation & board reporting",
  description: "Daily psychrometric logs, comprehensive digital photo manifests, and transparent line-item closeout packages ready for direct submission to insurance adjusters and HOA boards.",
  badge: "Board-ready closeout file",
  icon: FileCheck2
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-process" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-6 mb-10 border-b border-border">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            Active Commercial Dispatch • MD / DC / NOVA
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground font-mono">
            <Clock className="size-3.5 text-primary" />
            <span>24/7 Rapid Emergency Containment</span>
          </div>
        </div>

        {/* Section Heading & Overview */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Our commercial restoration process
          </h2>
          <p className="mt-3 text-lg text-muted-foreground font-default">
            Four structured stages designed to minimize tenant disruption and return your property to safe operational status.
          </p>
          <p className="mt-4 text-base text-foreground/80 leading-relaxed font-default">
            When water pipes burst, rooftop units fail, or electrical fires affect commercial or multi-family properties, every hour of delay compounds losses. TriServe assigns dedicated local technicians to manage emergency containment, tenant safety, and direct insurance documentation from the first dispatch to final sign-off.
          </p>
        </div>

        {/* 4-Step Process Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => {
          const IconComponent = step.icon;
          return <motion.div key={step.stepNumber} data-index={index} variants={cardVariants} className="h-full">
                <Card className="h-full bg-muted/60 border border-border rounded-lg p-6 flex flex-col justify-between transition-[background-color,border-color,box-shadow] duration-200 hover:bg-card hover:border-primary hover:shadow-md">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-5">
                      <span className="font-mono text-xs font-bold px-2.5 py-1 rounded bg-secondary text-secondary-foreground">
                        STAGE {step.stepNumber}
                      </span>
                      <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary">
                        <IconComponent className="size-5" />
                      </div>
                    </div>

                    <CardHeader className="p-0 gap-2 mb-3">
                      <CardTitle className="text-lg font-bold text-foreground leading-snug font-default">
                        {step.title}
                      </CardTitle>
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">
                        {step.badge}
                      </span>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm text-muted-foreground leading-relaxed font-default">
                        {step.description}
                      </p>
                    </CardContent>
                  </div>

                  <div className="pt-6 mt-6 border-t border-border/70 flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span>TRI-SERVE SOP</span>
                    <span className="text-primary font-medium">STEP {index + 1} OF 4</span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Emergency Dispatch Banner / Direct CTA */}
        <div className="mt-12 md:mt-16 rounded-lg border border-border bg-card p-6 md:p-8 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-bold text-foreground font-default">
              Facing an active facility disaster or tenant emergency?
            </h3>
            <p className="text-sm text-muted-foreground font-default">
              Our regional crews are on standby 24/7 across Maryland, Washington DC, and Northern Virginia.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 h-10 px-5 text-sm w-full sm:w-auto">
              <a href="tel:8554356631" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call 855-435-6631</span>
              </a>
            </Button>

            <Button asChild variant="outline" className="border-input text-foreground hover:bg-muted font-medium h-10 px-5 text-sm w-full sm:w-auto">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request service</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}